import type { ICameraVideoTrack, IMicrophoneAudioTrack } from 'agora-rtc-react';
import AgoraRTC from 'agora-rtc-react';

import { StudioLogger } from '@studio/logger';

const LOG = new StudioLogger('agoraDeviceListeners');

/* eslint-disable no-unused-vars */
interface DispatchFunction {
  (action: any): void;
}

/* eslint-disable no-unused-vars */
interface Actions {
  handleVideoInputChanged: (...args: any[]) => any;
  handleAudioInputChanged: (...args: any[]) => any;
  showNotification: (...args: any[]) => any;
  addToAttendeeTiles: (...args: any[]) => any;
}

function setupVideoDeviceListeners(
  localCameraTrack: ICameraVideoTrack | null,
  dispatch: DispatchFunction,
  actions: Actions,
  attendeeId: string
) {
  if (!localCameraTrack) {
    return;
  }

  AgoraRTC.on('camera-changed', async (changedDevice: any) => {
    const switchDevice = await handleDeviceChange(
      'video',
      localCameraTrack,
      changedDevice,
      dispatch,
      actions.handleVideoInputChanged,
      actions.showNotification
    );
    // When we change the camera device, we need to update the tile so that the local video feed re-renders
    if (switchDevice) {
      dispatch(actions.addToAttendeeTiles(attendeeId, localCameraTrack));
    }
  });
}

function setupAudioDeviceListeners(
  localAudioTrack: IMicrophoneAudioTrack | null,
  dispatch: DispatchFunction,
  actions: Actions
) {
  if (!localAudioTrack) {
    return;
  }

  AgoraRTC.on('microphone-changed', async (changedDevice: any) => {
    await handleDeviceChange(
      'audio',
      localAudioTrack,
      changedDevice,
      dispatch,
      actions.handleAudioInputChanged,
      actions.showNotification
    );
  });
}

function setupPlaybackDeviceListener() {
  AgoraRTC.on('playback-device-changed', (info: any) => {
    LOG.info(`Playback device changed: ${JSON.stringify(info)}`);
  });
}

async function handleDeviceChange(
  deviceType: 'audio' | 'video',
  deviceTrack: IMicrophoneAudioTrack | ICameraVideoTrack,
  changedDevice: any,
  dispatch: DispatchFunction,
  handleDevice: Function,
  showNotification: Function
) {
  let switchDevice = false;
  const deviceNaming = deviceType === 'audio' ? 'microphone' : 'camera';
  const currentDeviceLabel = deviceTrack.getTrackLabel();

  LOG.info(`Device Change Detected for ${deviceNaming} ${changedDevice.device.label}`, {
    changedDevice
  });

  // When a new device is detected, switch to the new device
  if (changedDevice.state === 'ACTIVE') {
    try {
      await deviceTrack.setDevice(changedDevice.device.deviceId);
      dispatch(handleDevice(changedDevice.device.deviceId));
      switchDevice = true;
      LOG.info(`New ${deviceNaming} detected. Switching from ${currentDeviceLabel} to ${changedDevice.device.label}`);

      // show notification only if the device label is different aka switching to a brand new device
      if (currentDeviceLabel !== changedDevice.device.label) {
        dispatch(
          showNotification(
            `New ${deviceNaming} detected. Switching from ${currentDeviceLabel} to ${changedDevice.device.label}`,
            {
              timeout: 5000,
              type: 'alert'
            }
          )
        );
      }
    } catch (error) {
      LOG.error(`Error switching to new ${deviceNaming} device: ${error}`);
    }
  } else if (changedDevice.device.label === currentDeviceLabel) {
    let oldDevices;

    if (deviceType === 'audio') {
      oldDevices = await AgoraRTC.getMicrophones();
    } else {
      oldDevices = await AgoraRTC.getCameras();
    }

    // Switch to an existing device when the unplugged device is the current device
    if (oldDevices[0]) {
      await deviceTrack.setDevice(oldDevices[0].deviceId);
      dispatch(handleDevice(oldDevices[0].deviceId));
      switchDevice = true;

      LOG.info(
        `${deviceNaming} ${changedDevice.device.label} disconnected. Changing to default ${oldDevices[0].label}.`
      );
      dispatch(
        showNotification(`Your ${deviceNaming} ${changedDevice.device.label} disconnected. Just changed to default.`, {
          timeout: 5000,
          type: 'alert'
        })
      );
    }
  }
  return switchDevice;
}

export { setupAudioDeviceListeners, setupVideoDeviceListeners, setupPlaybackDeviceListener, handleDeviceChange };
